import { cloneDeep, isNil } from 'lodash';
import * as yup from 'yup';
import { RolePermissionInputType } from '../../../../components/role/role.types';
import { logger } from '../../../../util/logger';
import { Pose, PoseUtil } from '../../../../util/pose.util';
import {
  FlowItem,
  FlowItemParameterAdjustmentMetadata,
  FlowItemParameterAdjustmentRange,
  FlowItemParameterMetadata,
  FlowItemParameterRef,
  FlowItemSharedParameterRef,
  FlowItemSharedParameterTag,
  ParameterValue,
  ParametersForm,
  SystemUnit,
  WaitForInputType,
} from '../model/flow.types';
import { GetYupSchemaForItemContext } from '../service/service.types';
import { convertUnits } from './math';

const getParameterValue = <T>(
  flowItem: FlowItem,
  ref: FlowItemParameterRef
): T => {
  const parameter = flowItem.parameters.find((p) => p.ref === ref);
  return parameter?.value as T;
};

const getParameterValueFromForm = <T>(
  form: ParametersForm,
  ref: FlowItemParameterRef
): T | undefined => {
  return form[ref]?.value as T;
};

const setParameterValueOnForm = <T>({
  form,
  ref,
  value,
  updateBaseValue = false,
}: {
  form: ParametersForm;
  ref: FlowItemParameterRef;
  value: T;
  updateBaseValue?: boolean;
}): void => {
  if (updateBaseValue) {
    // update all values if updateBaseValue is true regardless of whether the
    // parameter exists in the form
    form[ref] = {
      value,
      originalValue: value,
      baseValue: value,
      originalBaseValue: value,
    };
  } else if (!isNil(form[ref])) {
    // update only the value if the parameter exists in the form
    form[ref] = {
      ...form[ref],
      value,
      originalValue: value,
    };
  } else {
    logger.warn(
      'Could not set parameter value on form. The parameter does not exist.',
      {
        ref,
        form,
        value,
        updateBaseValue,
      }
    );
  }
};

const getParameterBaseValue = <T>(
  flowItem: FlowItem,
  ref: FlowItemParameterRef
): T => {
  const parameter = flowItem.parameters.find((p) => p.ref === ref);
  return parameter?.baseValue as T;
};

const getParameterBaseValueFromForm = <T>(
  form: ParametersForm,
  ref: FlowItemParameterRef
): T | undefined => {
  return form[ref]?.baseValue as T;
};

const buildYupParameterSchema = (
  value: ParameterValue,
  system: SystemUnit = SystemUnit.IMPERIAL,
  options?: { minimum?: number; maximum?: number }
): yup.AnySchema | undefined => {
  const { uom } = value;
  const { minimum: min, maximum: max } = options || {};

  if (value.type === 'number') {
    let schema = yup.number();

    if (typeof value.minimum === 'number') {
      let minimum = value.minimum;

      if (uom?.system) {
        minimum = convertUnits(minimum, uom.target, uom.system[system], {
          precision: uom.precision,
        });
      }

      schema = schema.min(Math.max(minimum, min ?? minimum));
    } else if (typeof min === 'number') {
      schema = schema.min(min);
    }

    if (typeof value.maximum === 'number') {
      let maximum = value.maximum;

      if (uom?.system) {
        maximum = convertUnits(maximum, uom.target, uom.system[system], {
          precision: uom.precision,
        });
      }

      schema = schema.max(Math.min(maximum, max ?? maximum));
    } else if (typeof max === 'number') {
      schema = schema.max(max);
    }

    if (value.required) {
      schema = schema.required();
    }

    return schema;
  } else if (value.type === 'boolean') {
    let schema = yup.boolean();

    if (value.required) {
      schema = schema.required();
    }

    return schema;
  } else if (value.type === 'string') {
    let schema = yup.string();

    if (value.required) {
      schema = schema.required();
    }

    return schema;
  } else if (value.type === 'array') {
    let schema = yup.array();

    if (value.required) {
      schema = schema.required();
    }

    return schema;
  } else if (value.type === 'object' && value.props) {
    const { props } = value;
    const propertyValueSchemaShape = Object.keys(props).reduce(
      (shape, propertyName) => {
        const schema = buildYupParameterSchema(props[propertyName], system);

        return {
          ...shape,
          [propertyName]: schema,
        };
      },
      {}
    );

    return value.required
      ? yup.object().required().shape(propertyValueSchemaShape)
      : yup.object().shape(propertyValueSchemaShape);
  }
};

const getFlowItemParameterSchemaRange = (params: {
  context: GetYupSchemaForItemContext;
  adjustmentMetadata: FlowItemParameterAdjustmentMetadata;
  ref: FlowItemParameterRef;
}): FlowItemParameterAdjustmentRange | undefined => {
  const { context, adjustmentMetadata: bound, ref } = params;
  const baseValue = getParameterBaseValueFromForm(context.form.parameters, ref);
  const parameter = context.form.parameters[ref];

  // This should never happen, but if it does we need to log it and fix data upstream
  if (isNil(baseValue)) {
    if (isNil(parameter)) {
      logger.warn(
        'Could not get bounds for parameter. The form parameter is nil.',
        {
          ref,
        }
      );
    } else if (isNil(parameter.value)) {
      logger.warn(
        'Could not get bounds for parameter. The form parameter has no set values.',
        {
          ref,
          parameter,
        }
      );
    } else {
      logger.error(
        'Could not get bounds for parameter: "baseValue" was never set. Defaulting to value as min/max.',
        {
          ref,
          parameter,
        }
      );

      const value = getParameterValueFromForm(context.form.parameters, ref);
      const convertedValue = Number(value);

      if (!isFinite(convertedValue)) {
        logger.error(
          'Could not get backup bounds for parameter: "value" is nil. No bounds will be set.',
          {
            ref,
            form: context.form,
          }
        );
        return;
      }

      return {
        minimum: convertedValue,
        maximum: convertedValue,
      };
    }
  }

  const base = Number(baseValue);

  if (!isFinite(base)) {
    return;
  }

  // This should never happen, but is a last resort to prevent errors. The admin
  // UI has schema validation and the service checks invalid user data as well
  // to prevent this scenario.
  if (isNil(bound.value)) {
    logger.error('The adjustment metadata is missing a value.', {
      ref,
      adjustmentMetadata: bound,
    });

    return {
      maximum: base,
      minimum: base,
    };
  }

  if (bound.type === RolePermissionInputType.NUMBER) {
    return {
      maximum: base + bound.value,
      minimum: base - bound.value,
    };
  } else if (bound.type === RolePermissionInputType.PERCENTAGE) {
    const difference = (bound.value / 100) * base;
    return {
      maximum: base + difference,
      minimum: base - difference,
    };
  }
};

const convertParameterValueMetadataToSystem = (
  value: ParameterValue,
  system: SystemUnit
): ParameterValue => {
  if (value.type === 'number' && value.uom) {
    if (value.default) {
      value.default = convertParameterValueToUOM(
        value,
        system,
        value.default,
        false
      );
    }

    if (value.minimum) {
      value.minimum = convertParameterValueToUOM(
        value,
        system,
        value.minimum,
        false
      );
    }

    if (value.maximum) {
      value.maximum = convertParameterValueToUOM(
        value,
        system,
        value.maximum,
        false
      );
    }
  }

  return value;
};

const convertParameterValueToUOM = (
  propertyValue: ParameterValue,
  system: SystemUnit,
  currentValue: number,
  convertToTarget: boolean
): number => {
  const { type, uom } = propertyValue;

  if (uom?.system && type === 'number') {
    if (convertToTarget) {
      // convert with higher precision back to target so we don't lose data
      return convertUnits(currentValue, uom.system[system], uom.target, {
        precision: 8,
      });
    } else {
      return convertUnits(currentValue, uom.target, uom.system[system], {
        precision: uom.precision,
      });
    }
  }

  return currentValue;
};

const convertParameterMetadata = (
  metadata: FlowItemParameterMetadata,
  systemUnit: SystemUnit
): FlowItemParameterMetadata => {
  const cloned = cloneDeep(metadata);

  cloned.value = convertParameterValueMetadataToSystem(
    cloned.value,
    systemUnit
  );

  return cloned;
};

const getParametersFormWaitForTags = (form: ParametersForm): string[] => {
  const waitForType: WaitForInputType | undefined = getParameterValueFromForm(
    form,
    FlowItemSharedParameterRef.WAIT_FOR_TYPE_OF_INPUT
  );

  const tags: string[] = [];

  if (waitForType === WaitForInputType.OPERATOR_BUTTON) {
    tags.push(FlowItemSharedParameterTag.WAIT_FOR_OPERATOR);
  } else if (waitForType === WaitForInputType.SENSOR) {
    tags.push(FlowItemSharedParameterTag.WAIT_FOR_INPUT);
  } else if (waitForType === WaitForInputType.TIME) {
    tags.push(FlowItemSharedParameterTag.WAIT_FOR_TIME);
  }

  return tags;
};

const addStickoutToPose = (pose: Pose, stickout: number): Pose => {
  return PoseUtil.pose_trans(pose, [0, 0, stickout / 1000, 0, 0, 0]);
};

export {
  addStickoutToPose,
  buildYupParameterSchema,
  convertParameterMetadata,
  convertParameterValueMetadataToSystem,
  convertParameterValueToUOM,
  getParametersFormWaitForTags,
  getParameterValue,
  getParameterValueFromForm,
  getParameterBaseValue,
  getParameterBaseValueFromForm,
  getFlowItemParameterSchemaRange,
  setParameterValueOnForm,
};
